import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  capitalize,
  CircularProgress,
  IconButton,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {GridCellParams, GridColDef, GridSortModel} from '@mui/x-data-grid-pro';
import {Delete, FolderOpen, DeleteForever} from '@mui/icons-material';
import React, {useState} from 'react';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {useAccountContext} from '../../contexts/AccountContext';
import {themeColors} from '../../theme/Theme';
import {RenderColor} from '../EditorGrid/GridRenderComponents';
import EditorGrid from '../EditorGrid/EditorGrid';
import {renderStatus} from '../EditorGrid/EditorGridStatus';
import {Review} from '../../schema-types/Review';
import {AccountReviews_reviews} from '../../schema-types/AccountReviews';
import Loading from '../Loading/Loading';
import {useAccountReviews} from '../../hooks/useAccountReviews';
import {useLocalStorage} from '../../hooks/useLocalStorage';
import {reviewTypeValuesToLabels} from '../PrecursorForm/PrePrecursorHelper';
import {SIX_MONTHS_AHEAD_IN_DAYS, dateDifferenceInDays} from '../../utils/dateUtils';
import useDjango from '../../hooks/useDjango';
import {isDevOrTester} from '../../contexts/useAuthContext';
import useReSyncReview from '../../hooks/useReSyncReview';
import {newUTCDate} from '../../utils/newUTCDate';
import {renderCellDate} from '../../utils/renderCellDate';
import HistoryDiffDialog from '../AccountReviewDialog/HistoryDiffDialog';
import {GlobalPermissionEnum, OVModelsEnum} from '../../schema-types/globalTypes';
import {HistoryQueryType} from '../AccountReviewDialog/useHistoryDiff';
import {getEnvironment} from '../../utils/envTest';
import {FeatureBoundary, hasPermission} from '../../contexts/FeatureBoundary';
import {SaltusAny} from '../../utils/SaltusAny';
import {useDeleteAccounts} from '../../hooks/useDeleteAccounts';
import {GET_ACCOUNT_REVIEWS} from '../AccountOverview/accountOverviewQueries';
import {PrintRiskAndNotes} from '../../features/risk/PrintableRiskAndNotes';
import {ShowIfDebugWrapper, useDebugContext} from '../../contexts/DebugProvider';
import validateUUID from '../../utils/validateUUID';

const iconColumnWidth = 70;

export const getOwnerInfo = (
  row: AccountReviews_reviews | null | undefined
): {displayName: string; type: 'host' | 'owner' | null} => {
  if (row?.invitation?.invitationOwner?.name) {
    return {displayName: row.invitation.invitationOwner.name, type: 'host'};
  }
  if (row?.owner?.name) {
    return {displayName: row.owner.name, type: 'owner'};
  }
  return {displayName: 'N/A', type: null};
};

export const renderOwnerCell = (
  row: AccountReviews_reviews | null | undefined
): React.ReactNode => {
  const {displayName, type} = getOwnerInfo(row);

  if (type) {
    return (
      <Tooltip title={type === 'host' ? 'Host' : 'Owner'} placement="right">
        <span>{displayName}</span>
      </Tooltip>
    );
  }
  return 'N/A';
};

const OpenButton: React.FC<{reviewId: string; dateScheduled: string}> = (props) => {
  const {reviewId, dateScheduled} = props;
  const {enqueueSnackbar} = useSnackbar();
  return (
    <Box>
      <Button
        component={RouterLink}
        to={`../review-journey/${reviewId}`}
        variant="outlined"
        size="small"
        type="button"
        onClick={() => {
          if (!dateScheduled) {
            return;
          }
          const differenceInDays = dateDifferenceInDays({
            date: newUTCDate(dateScheduled),
            today: newUTCDate(),
          });
          /**
           * add a warning if review is scheduled more than 6 months in advance
           */
          if (differenceInDays >= SIX_MONTHS_AHEAD_IN_DAYS) {
            enqueueSnackbar({
              message:
                'This review is scheduled in more than 6 months from now. Are you sure it is the correct review?',
              variant: 'warning',
              persist: true,
            });
          }
        }}
      >
        Open
      </Button>
    </Box>
  );
};

const columns: (GridColDef & {
  field: keyof AccountReviews_reviews;
})[] = [
  {
    field: 'action' as any,
    headerName: 'Action',
    flex: 1,
    editable: false,
    sortable: false,
    headerAlign: 'center',
    // eslint-disable-next-line react/destructuring-assignment
    renderCell: (params) => (
      <Box width="100%" display="flex" justifyContent="center">
        <OpenButton reviewId={params.row.id} dateScheduled={params.row.dateScheduled} />
      </Box>
    ),
  },
  {
    field: 'statusOneview',
    headerName: 'Status',
    flex: 1,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    renderCell: renderStatus,
  },
  {
    field: 'reviewReason',
    headerName: 'Type',
    flex: 1,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    renderCell: RenderColor({
      Annual: themeColors.lightGreen,
      'Ad hoc': themeColors.lightBlue,
      Initial: themeColors.lightYellow,
      Other: themeColors.greyLight,
      default: themeColors.greyLight,
    }),
    valueGetter: (value: SaltusAny, row, column, apiRef) => {
      const val = value?.toString();
      if (!val) {
        return 'Other';
      }
      if (val in reviewTypeValuesToLabels) {
        // do nothing, just here to shut typescript up
      } else {
        return `Other`;
      }
      return capitalize(
        val in reviewTypeValuesToLabels
          ? reviewTypeValuesToLabels[val as keyof typeof reviewTypeValuesToLabels]
          : val
      );
    },
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 3,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    renderCell: (params) => (
      <div
        style={{
          textAlign: 'left',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1,
        }}
      >
        <Button
          component={RouterLink}
          to={`../review-journey/${params.id}`}
          variant="text"
          size="small"
          startIcon={<FolderOpen />}
          type="button"
          style={{textAlign: 'left'}}
        >
          {/* eslint-disable-next-line react/destructuring-assignment */}
          {params?.row?.name?.toLowerCase() === 'undefined' || !params?.row?.name
            ? '[No name given]'
            : // eslint-disable-next-line react/destructuring-assignment
              params.row.name}
        </Button>
        <FeatureBoundary
          features={[GlobalPermissionEnum.can_access_limited_features]}
          showErrorIfDenied={false}
        >
          <ShowIfDebugWrapper>
            OV: {params.row.id}
            <br />
            Curo ID: {params.row.curoId}
            <br />
          </ShowIfDebugWrapper>
        </FeatureBoundary>
      </div>
    ),
  },
  {
    field: 'dateBooked',
    headerName: 'Booked',
    flex: 1,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    renderCell: renderCellDate,
  },
  {
    field: 'dateScheduled',
    headerName: 'Scheduled',
    flex: 1,
    renderCell: renderCellDate,
    editable: false,
    sortable: true,
    headerAlign: 'center',
  },
  {
    field: 'dateCreated',
    headerName: 'Created',
    type: 'date',
    flex: 1,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    renderCell: renderCellDate,
    valueGetter: (value, row, column, apiRef) => newUTCDate(value as any),
    valueFormatter: (value: Date) => value.toLocaleDateString(),
  },
  // {
  //   field: 'stage',
  //   headerName: 'Project Stage',
  //   flex: 2,
  //   editable: false,
  //   sortable: true,
  //   headerAlign: 'center',
  //   valueGetter: (value, row, column, apiRef) => {
  //     const this_row = row as Review;
  //     return capitalize(this_row.stage?.replaceAll('_', ' ').toLocaleLowerCase() || '');
  //   },
  //   renderCell: RenderColor({
  //     'need booking': themeColors.lightAlert,
  //     booked: themeColors.lightYellow,
  //     'awaiting handover': themeColors.lightPurple,
  //     'being implemented': themeColors.lightGreen,
  //   }),
  // },
  {
    field: 'adviser',
    headerName: 'Owner / Host',
    flex: 1,
    editable: false,
    sortable: true,
    headerAlign: 'center',
    valueGetter: (value, row, column, apiRef) => getOwnerInfo(row).displayName,
    renderCell: (params) => renderOwnerCell(params.row),
  },
];

const PrintReviewColumn: React.FC<{
  accountId: string;
  accountName: string;
  reviewId: string;
  contactId?: string;
  isTrust: boolean;
}> = ({accountId, accountName, reviewId, contactId, isTrust}) => {
  return (
    <PrintRiskAndNotes
      isTrust={isTrust}
      accountId={accountId}
      accountName={accountName}
      reviewId={reviewId}
      contactId={contactId}
    />
  );
};

const AccountReviewsGrid: React.FC<{
  createReview: boolean | (() => void);
  createReviewLoading: boolean;
  deleteReview: (review: AccountReviews_reviews) => SaltusAny;
  onSelected: (args: SaltusAny) => SaltusAny;
  deleteLoading: boolean;
}> = (props) => {
  const {account, accountId, accountLoading, accountError} = useAccountContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const [resyncing, setResyncing] = useState(false);
  const [onlyCurrent, setOnlyCurrent] = useLocalStorage<boolean>(
    'account-reviews-current-only-28-06-2023',
    false
  );
  const [filters, setFilters] = useLocalStorage<GridSortModel>('reviewGrid', [
    {field: 'dateScheduled', sort: 'desc'},
    {field: 'isDraft', sort: 'desc'},
  ]);

  const {createReview, deleteReview, onSelected, createReviewLoading, deleteLoading} =
    props;
  const env = getEnvironment();
  const syncReviews = env === 'PROD' || env === 'UAT';
  const {accountReviews, refetchAccountReviews, accountReviewsLoading} =
    useAccountReviews({
      variables: {
        accountId: validateUUID(accountId!)!,
        idType: 'id',
        onlyCurrent,
        curoSync: onlyCurrent ? syncReviews : true,
        includeProgressSync: false,
      },
      skip: typeof onlyCurrent !== 'boolean', // dont fetch until onlyCurrect is fetched from localstorage
    });

  const handleDialogOpen = (id: string) => {
    setSelectedReviewId(id);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setSelectedReviewId('');
    setDialogOpen(false);
  };
  const {debug} = useDebugContext();
  const {reSyncReview} = useReSyncReview(onlyCurrent);
  const {enqueueSnackbar} = useSnackbar();
  const {djangoUser} = useDjango();
  const navigate = useNavigate();
  const {deleteReviewsForAccount} = useDeleteAccounts();

  const reviewHistoryColumn: GridColDef<AccountReviews_reviews, any, any> = {
    field: 'history',
    headerName: 'History',
    width: iconColumnWidth,
    editable: false,
    disableColumnMenu: true,
    sortable: false,
    headerAlign: 'center',
    renderCell: (params: GridCellParams) => {
      return (
        <IconButton
          aria-label="print"
          color="primary"
          size="large"
          sx={{margin: 0}}
          onClick={() => handleDialogOpen(params.id.toString())}
        >
          <ScheduleIcon style={{cursor: 'pointer'}} data-testid="history-review" />
        </IconButton>
      );
    },
  };

  const printReviewColumn: GridColDef<AccountReviews_reviews> = {
    field: 'print',
    headerName: 'Print',
    width: iconColumnWidth,
    editable: false,
    sortable: false,
    headerAlign: 'center',
    disableColumnMenu: true,

    renderCell: (params: GridCellParams<AccountReviews_reviews>) => {
      const review = params.row;
      const isTrust = account?.isTrust || false;
      return (
        <PrintReviewColumn
          accountId={accountId!}
          accountName={account?.name || 'Client'}
          reviewId={review.id}
          // contactId={validateUUID(review?.contact?.id)}
          isTrust={isTrust}
        />
      );
    },
  };

  const envColumns = [
    ...columns,
    ...(hasPermission(djangoUser, [GlobalPermissionEnum.can_access_admin_features])
      ? [reviewHistoryColumn]
      : []),
    ...(hasPermission(djangoUser, [GlobalPermissionEnum.can_access_limited_features])
      ? [printReviewColumn]
      : []),
    {
      field: 'id',
      headerName: ' ',
      width: iconColumnWidth,
      disableColumnMenu: true,
      sortable: false,
      editable: false,
      renderCell: (params: GridCellParams) => {
        const row = params.row as AccountReviews_reviews;
        if (!row.curoId || isDevOrTester(djangoUser)) {
          return (
            <Delete
              style={{cursor: 'pointer', opacity: deleteLoading ? 0.5 : 1}}
              color="action"
              onClick={() => (deleteLoading ? null : deleteReview(row))}
            />
          );
        }

        return (
          <Tooltip title="Only non-submitted reviews can be deleted. Please deactivate it in Curo first">
            <DeleteForever style={{opacity: 0.5}} color="error" />
          </Tooltip>
        );
      },
    },
  ];

  if (!accountReviews?.reviews) {
    return <Loading entityName="reviews" />;
  }
  return (
    <>
      <FormControlLabel
        label="Show only current reviews"
        control={
          <Checkbox
            checked={onlyCurrent}
            onChange={(x) => setOnlyCurrent(x.target.checked)}
          />
        }
      />
      <EditorGrid
        dataGridId="accountReviews"
        error={accountError}
        loading={accountLoading}
        rowHeight={debug?.forms ? 80 : undefined}
        rows={accountReviews?.reviews || []}
        onRowDoubleClick={(row) => {
          navigate({
            pathname: `/account/${account?.id}/review-journey/${row.id}`,
          });
        }}
        columns={envColumns}
        createFunc={createReview}
        createFuncLoading={createReviewLoading}
        deleteFuncLoading={deleteLoading}
        syncFunc={
          // Show if role == tester:
          isDevOrTester(djangoUser)
            ? (reviewID: string | null) => {
                if (!reviewID) {
                  enqueueSnackbar({
                    message: 'Please select a review first',
                    variant: 'error',
                    autoHideDuration: null,
                  });
                  return;
                }
                enqueueSnackbar({
                  message: 'Syncing specific review...',
                  variant: 'info',
                });
                reSyncReview({
                  variables: {
                    reviewID,
                  },
                });
              }
            : false
        }
        onSelected={onSelected}
        minHeight="600px"
        sortModel={filters}
        onSortModelChange={(params) => {
          setFilters(params);
        }}
        updateFunc={false}
      >
        <Button
          onClick={() => {
            setResyncing(true);
            refetchAccountReviews({
              accountId: validateUUID(accountId!)!,
              curoSync: true,
              idType: 'id',
              onlyCurrent,
            }).finally(() => setResyncing(false));
          }}
        >
          {resyncing ? (
            <>
              Syncing...
              <CircularProgress size={20} />
            </>
          ) : (
            'Sync reviews'
          )}
        </Button>
        {env !== 'PROD' && (
          <Button
            onClick={async () => {
              await deleteReviewsForAccount({
                variables: {
                  accountId: account?.id,
                },
                refetchQueries: [
                  {
                    query: GET_ACCOUNT_REVIEWS,
                    variables: {
                      accountId: validateUUID(accountId!)!,
                      idType: 'id',
                      curoSync: true,
                    },
                  },
                ],
              }).then(({data}) => {
                if (data?.deleteReviewsForAccount?.ok) {
                  enqueueSnackbar({
                    message: 'Deleted all reviews for account',
                    variant: 'success',
                  });
                  return;
                }
                enqueueSnackbar({
                  message: 'Could not delete all reviews for account',
                  variant: 'error',
                });
              });
            }}
          >
            Delete Reviews For Account
          </Button>
        )}
      </EditorGrid>
      <HistoryDiffDialog
        isOpen={dialogOpen}
        handleClose={handleDialogClose}
        entityId={selectedReviewId}
        entityType={OVModelsEnum.REVIEW}
        queryType={HistoryQueryType.GENERIC}
      />
    </>
  );
};

export default AccountReviewsGrid;
